import { fmtDateTime } from '@/Utils/siteSchedule'
import { canonicalTimeZone } from '@/components/Sites/siteTemplates'

// Broadcast stage schedule maths for the dashboard (Trello O7661FGg). Mirrors
// the runtime's `lib/sites/broadcast-schedule.ts` closely enough to warn the
// owner before saving: weekly service times are read in the site's zone, and
// a stream's own scheduled start replaces the service time(s) on the same
// local day. The site counts down to the earliest of the stream's schedule,
// its Calendar entries and the next service time.

const TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/
const DAY_MS = 86400000
const LOOKAHEAD_DAYS = 15

function zonedParts(timeZone, at) {
	const parts = new Intl.DateTimeFormat('en-US', { timeZone, hourCycle: 'h23', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric', weekday: 'short' }).formatToParts(at)
	const get = (type) => parts.find((p) => p.type === type)?.value
	return {
		year: Number(get('year')),
		month: Number(get('month')),
		day: Number(get('day')),
		hour: Number(get('hour')) % 24,
		minute: Number(get('minute')),
		second: Number(get('second')),
		weekday: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(get('weekday')),
	}
}

function offsetMs(timeZone, at) {
	const p = zonedParts(timeZone, at)
	return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - Math.floor(at.getTime() / 1000) * 1000
}

/** The instant `timeZone` reads that wall-clock time (gap → the next real instant, overlap → the first). */
function zonedInstant(timeZone, wall) {
	const target = Date.UTC(wall.year, wall.month - 1, wall.day, wall.hour, wall.minute)
	const candidates = [...new Set([-DAY_MS, 0, DAY_MS].map((d) => offsetMs(timeZone, new Date(target + d))))].map((off) => target - off).sort((a, b) => a - b)
	const exact = candidates.filter((c) => {
		const p = zonedParts(timeZone, new Date(c))
		return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute) === target
	})
	return new Date(exact[0] ?? candidates[candidates.length - 1])
}

/** `YYYY-MM-DD` of an instant in the zone. */
export function localDayKey(timeZone, at) {
	const p = zonedParts(timeZone, at instanceof Date ? at : new Date(at))
	return `${p.year}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`
}

function validZone(timeZone) {
	try {
		new Intl.DateTimeFormat('en-US', { timeZone })
		return Boolean(timeZone)
	} catch {
		return false
	}
}

/**
 * Next weekly service start strictly after `now` (ISO), skipping occurrences
 * for which `skip(date)` is true, or null.
 * @param {{ weekday: number, time: string }[]} serviceTimes
 */
export function nextServiceStart(serviceTimes, timeZone, now = new Date(), skip = null) {
	if (!Array.isArray(serviceTimes) || !validZone(timeZone)) return null
	const today = zonedParts(timeZone, now)
	let best = null
	for (const entry of serviceTimes) {
		const m = TIME_RE.exec(entry?.time || '')
		if (!m || !Number.isInteger(entry.weekday) || entry.weekday < 0 || entry.weekday > 6) continue
		for (let i = 0; i <= LOOKAHEAD_DAYS; i++) {
			const d = new Date(Date.UTC(today.year, today.month - 1, today.day + i))
			if (d.getUTCDay() !== entry.weekday) continue
			const start = zonedInstant(timeZone, { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate(), hour: Number(m[1]), minute: Number(m[2]) })
			if (start.getTime() <= now.getTime() || skip?.(start)) continue
			if (!best || start < best) best = start
			break
		}
	}
	return best ? best.toISOString() : null
}

/**
 * Does a live stream's scheduled start disagree with the other sources that
 * drive the Broadcast countdown? Returns the earliest disagreeing source
 * (`service` — the next service time on another local day; `calendar` —
 * another Calendar entry of the same stream on another instant) or null.
 *
 * @param {object} input
 * @param {string|null} input.scheduledStart ISO
 * @param {{ weekday: number, time: string }[]} [input.serviceTimes]
 * @param {string} [input.timeZone] site zone (`broadcastTimezone`)
 * @param {string[]} [input.otherEntryStarts] ISO starts of the stream's other Calendar entries
 * @param {Date} [input.now]
 * @returns {{ scheduledStart: string, otherStart: string, source: 'service'|'calendar' } | null}
 */
export function broadcastScheduleConflict({ scheduledStart, serviceTimes = [], timeZone = 'UTC', otherEntryStarts = [], now = new Date() }) {
	const scheduled = scheduledStart ? new Date(scheduledStart) : null
	if (!scheduled || Number.isNaN(scheduled.getTime()) || scheduled.getTime() <= now.getTime()) return null
	const zone = validZone(timeZone) ? timeZone : 'UTC'
	const scheduledDay = localDayKey(zone, scheduled)
	const service = nextServiceStart(serviceTimes, zone, now, (start) => localDayKey(zone, start) === scheduledDay)
	const calendar = otherEntryStarts
		.map((s) => new Date(s))
		.filter((d) => !Number.isNaN(d.getTime()) && d.getTime() > now.getTime() && d.getTime() !== scheduled.getTime())
		.sort((a, b) => a - b)[0]
	const candidates = [
		service ? { source: 'service', at: new Date(service) } : null,
		calendar ? { source: 'calendar', at: calendar } : null,
	].filter(Boolean).sort((a, b) => a.at - b.at)
	const other = candidates[0]
	return other ? { scheduledStart: scheduled.toISOString(), otherStart: other.at.toISOString(), source: other.source } : null
}

/**
 * Id of the item in the big player: the pinned `broadcastLiveItemId`, else
 * (Automatic) the first published live item in Library order; 0 when there is
 * none yet, so whichever live item is added next becomes the main stream.
 */
export function mainLiveItemId(liveItemId, items = []) {
	const pinned = Number(liveItemId) || 0
	if (pinned) return pinned
	const first = items.find((i) => i?.published !== false && (i?.castr_ref?.type === 'live' || i?.type === 'live'))
	return first ? Number(first.id) || 0 : 0
}

// Wording on the site (Trello uD29pZf3): "Next service" names the weekly
// service times only; any other dated start is a one-off ("Next broadcast" /
// "Special event: <title>"). A dated start on a service time (same weekday and
// HH:MM in the site zone) counts as that service.

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

function clock12(hour, minute) {
	return `${hour % 12 || 12}:${String(minute).padStart(2, '0')} ${hour < 12 ? 'AM' : 'PM'}`
}

/** The service-time entry `at` lands on in the site zone, or null. */
export function matchingServiceTime(at, serviceTimes, timeZone) {
	const date = at instanceof Date ? at : new Date(at)
	if (!Array.isArray(serviceTimes) || Number.isNaN(date.getTime()) || !validZone(timeZone)) return null
	const p = zonedParts(timeZone, date)
	return serviceTimes.find((entry) => {
		const m = TIME_RE.exec(entry?.time || '')
		return m && entry.weekday === p.weekday && Number(m[1]) === p.hour && Number(m[2]) === p.minute
	}) || null
}

/** "Sunday 9:00 AM" */
export function fmtServiceTime(entry) {
	const m = TIME_RE.exec(entry?.time || '')
	if (!m || !WEEKDAYS[entry.weekday]) return ''
	return `${WEEKDAYS[entry.weekday]} ${clock12(Number(m[1]), Number(m[2]))}`
}

/** "Dhaka time" / "Buenos Aires time" / "UTC" — the site zone as the public site names it. */
export function zonePlaceName(timeZone) {
	timeZone = canonicalTimeZone(timeZone)
	if (!timeZone || timeZone === 'UTC' || timeZone === 'Etc/UTC') return 'UTC'
	return `${timeZone.split('/').pop().replace(/_/g, ' ')} time`
}

/**
 * "Fri 8:00 PM" in the site zone, as the public site prints it; the zone is
 * added when the browser's clock reads differently at that instant.
 */
export function fmtSiteDayTime(at, timeZone, browserZone = Intl.DateTimeFormat().resolvedOptions().timeZone) {
	const date = at instanceof Date ? at : new Date(at)
	if (Number.isNaN(date.getTime())) return ''
	const zone = validZone(timeZone) ? timeZone : 'UTC'
	const p = zonedParts(zone, date)
	const text = `${WEEKDAYS[p.weekday].slice(0, 3)} ${clock12(p.hour, p.minute)}`
	const b = validZone(browserZone) ? zonedParts(browserZone, date) : null
	const sameClock = b && b.day === p.day && b.hour === p.hour && b.minute === p.minute
	return sameClock ? text : `${text} ${zonePlaceName(zone)}`
}

/**
 * How viewers will see a chosen start on a Broadcast site (non-blocking notice):
 * `match` — on a service time; `outside` — a one-off "Next broadcast";
 * `none` — no service times yet (the owner is pointed at Settings › Broadcast).
 * Null for a missing or past start.
 *
 * @returns {{ kind: 'match'|'outside'|'none', text: string } | null}
 */
export function serviceTimeNotice({ start, serviceTimes = [], timeZone = 'UTC', now = new Date() }) {
	const date = start ? new Date(start) : null
	if (!date || Number.isNaN(date.getTime()) || date.getTime() <= now.getTime()) return null
	const zone = validZone(timeZone) ? timeZone : 'UTC'
	const valid = (Array.isArray(serviceTimes) ? serviceTimes : []).filter((e) => TIME_RE.test(e?.time || '') && WEEKDAYS[e?.weekday])
	if (!valid.length) return { kind: 'none', text: 'Add service times in Settings › Broadcast so viewers know your regular schedule.' }
	const match = matchingServiceTime(date, valid, zone)
	if (match) {
		const zoneNote = fmtSiteDayTime(date, zone).endsWith(zonePlaceName(zone)) ? ` (${zonePlaceName(zone)})` : ''
		return { kind: 'match', text: `Matches your ${fmtServiceTime(match)} service${zoneNote}.` }
	}
	return {
		kind: 'outside',
		text: `This is outside your regular service times. Viewers will see it as a special broadcast (‘Next broadcast · ${fmtSiteDayTime(date, zone)}’), and your next service still shows in the header.`,
	}
}

/** The owner-facing sentence for a conflict (non-blocking). */
export function broadcastConflictMessage(conflict) {
	if (!conflict) return ''
	const what = conflict.source === 'calendar' ? `your Calendar lists it on ${fmtDateTime(conflict.otherStart)}` : `your service times say the next service is ${fmtDateTime(conflict.otherStart)}`
	return `This stream is scheduled for ${fmtDateTime(conflict.scheduledStart)}; ${what}. The site will count down to the earlier of the two.`
}
